import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import AISummaryModal from '@/components/ui/AISummaryModal'
import AIRecommendationsPanel from '@/components/ui/AIRecommendationsPanel'
import type { ClearingDocItem } from './ClearingDocumentCell'

export interface ClearingShipmentData {
  id: string
  shipmentId: string
  containerNumber: string
  shippingLine: string
  origin: string
  destination: string
  arrivalDate: string
  documentStatus: string
  status: string
  clearingCost?: string
}

interface ClearingShipmentDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  shipmentData?: ClearingShipmentData
  mode?: 'view' | 'edit'
  onSave?: (updated: ClearingShipmentData) => Promise<void> | void
  onUploadDocument?: (type: 'shipping' | 'clearing' | 'bank') => void
  onViewDocuments?: (type: 'shipping' | 'clearing' | 'bank') => void
  documents?: ClearingDocItem[]
  isUploading?: boolean
}

const docUploadButtonStyle = {
  fontFamily: 'Lexend Deca',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '20px',
  color: '#212126',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E9E9EA',
  borderRadius: '6px',
  padding: '6px 10px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap' as const,
}

const ClearingShipmentDetailsModal = ({
  isOpen,
  onClose,
  shipmentData,
  mode = 'view',
  onSave,
  onUploadDocument,
  onViewDocuments,
  documents = [],
  isUploading = false,
}: ClearingShipmentDetailsModalProps) => {
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false)
  const [localDocStatus, setLocalDocStatus] = useState(shipmentData?.documentStatus ?? '')
  const [localClearingStatus, setLocalClearingStatus] = useState(shipmentData?.status ?? '')
  const [localClearingCost, setLocalClearingCost] = useState(shipmentData?.clearingCost ?? '')
  const isEdit = mode === 'edit'

  const docsForType = (type: 'shipping' | 'clearing' | 'bank') =>
    documents.filter((d) => d.type === type)

  const renderDocumentSection = (
    label: string,
    type: 'shipping' | 'clearing' | 'bank'
  ) => {
    const count = docsForType(type).length
    return (
      <div className="py-4 border-b border-gray-200 flex items-center justify-between gap-3">
        <div>
          <div style={{ fontFamily: 'Lexend Deca', fontWeight: 400, fontSize: '12px', lineHeight: '20px', color: '#69697A' }}>
            {label}
          </div>
          {count > 0 && (
            <div style={{ fontFamily: 'Lexend Deca', fontSize: '11px', color: '#13AB5B', marginTop: '4px' }}>
              {count} file{count !== 1 ? 's' : ''} uploaded
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {count > 0 && (
            <button
              type="button"
              onClick={() => onViewDocuments?.(type)}
              className="hover:opacity-90 transition-opacity"
              style={{ ...docUploadButtonStyle, minWidth: '52px' }}
            >
              View
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (isEdit) onUploadDocument?.(type)
            }}
            disabled={!isEdit || isUploading}
            className="hover:opacity-90 transition-opacity"
            style={{
              ...docUploadButtonStyle,
              opacity: !isEdit ? 0.6 : 1,
              cursor: !isEdit ? 'not-allowed' : 'pointer',
              minWidth: '88px',
            }}
            title={isEdit ? 'Select one or more PDF files' : undefined}
          >
            {!isEdit ? 'View only' : isUploading ? 'Uploading…' : count > 0 ? 'Add PDF(s)' : 'Upload PDF(s)'}
          </button>
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (shipmentData) {
      setLocalDocStatus(shipmentData.documentStatus)
      setLocalClearingStatus(shipmentData.status)
      setLocalClearingCost(shipmentData.clearingCost ?? '')
    }
  }, [shipmentData])

  if (!isOpen || !shipmentData) return null

  const handleSave = async () => {
    if (!onSave) return
    await onSave({
      ...shipmentData,
      documentStatus: localDocStatus,
      status: localClearingStatus,
      clearingCost: localClearingCost,
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
      case 'Paid':
      case 'Cleared':
        return { textColor: '#13AB5B', bgColor: '#E8F7EF' }
      case 'Pending':
      case 'On Hold':
        return { textColor: '#E93323', bgColor: '#FEE2E2' }
      case 'In Progress':
      case 'Missing BL':
        return { textColor: '#3B82F6', bgColor: '#E0E7FF' }
      default:
        return { textColor: '#69697A', bgColor: '#F2F4F2' }
    }
  }

  const docStatusColors = getStatusColor(shipmentData.documentStatus)
  const formatClearingStatusLabel = (status: string) => {
    const s = (status || '').toLowerCase()
    if (s === 'in_progress') return 'In Progress'
    if (s === 'on_hold') return 'On Hold'
    if (s === 'cleared') return 'Cleared'
    if (s === 'pending') return 'Pending'
    return status
  }

  const statusColors = getStatusColor(formatClearingStatusLabel(shipmentData.status))

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
      onClick={onClose}
      style={{ zIndex: 60 }}
    >
      <div 
        className="bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{
          fontFamily: 'Lexend Deca',
          width: '548px',
          maxWidth: '548px',
          height: '100vh',
          maxHeight: '100vh',
          zIndex: 70,
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between border-b border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <h2 
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '18px',
              lineHeight: '26px',
              letterSpacing: '0px',
              color: '#212126'
            }}
          >
            {isEdit ? 'Edit Shipment Details' : 'Shipment Details'}
          </h2>
          <div className="flex items-center gap-2">
            <button
            onClick={onClose}
            className="flex items-center justify-center hover:opacity-80 transition-opacity"
            style={{ 
              width: '24px',
              height: '24px'
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Outer circle */}
              <circle
                cx="12"
                cy="12"
                r="10.5"
                stroke="#212126"
                strokeWidth="1.5"
                fill="none"
              />
              {/* Inner X - top-left to bottom-right */}
              <line
                x1="9.17"
                y1="9.17"
                x2="14.83"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              {/* Inner X - top-right to bottom-left */}
              <line
                x1="14.83"
                y1="9.17"
                x2="9.17"
                y2="14.83"
                stroke="#212126"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          <div className="mb-4">
            <AIRecommendationsPanel
              entityType="clearing"
              entityId={shipmentData.id}
              agentType="CLEARING_AGENT"
            />
          </div>
          <div className="space-y-0">
            {/* Booking */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Booking
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.shipmentId}
              </div>
            </div>

            {/* Container Number */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Container Number
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.containerNumber}
              </div>
            </div>

            {/* Shipping Line */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Shipping Line
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.shippingLine}
              </div>
            </div>

            {/* Origin */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Origin
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.origin}
              </div>
            </div>

            {/* Destination */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Destination
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.destination}
              </div>
            </div>

            {/* Arrival Date */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Arrival Date
              </div>
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#212126'
                }}
              >
                {shipmentData.arrivalDate}
              </div>
            </div>

            {renderDocumentSection('Shipping documents', 'shipping')}

            {/* Document Status */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Document Status
              </div>
              {isEdit ? (
                <select
                  value={localDocStatus}
                  onChange={(e) => setLocalDocStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs sm:text-sm"
                  style={{ fontFamily: 'Lexend Deca', minWidth: '140px' }}
                >
                  <option value="Completed">Completed</option>
                  <option value="IN Draft">IN Draft</option>
                  <option value="IN Coordination">IN Coordination</option>
                  <option value="Pending">Pending</option>
                </select>
              ) : (
                <span 
                  className="inline-block text-xs font-medium"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '20px',
                    color: docStatusColors.textColor,
                    backgroundColor: docStatusColors.bgColor,
                    borderRadius: '6px',
                    padding: '4px 12px',
                    display: 'inline-block'
                  }}
                >
                  {shipmentData.documentStatus}
                </span>
              )}
            </div>

            {/* Clearing Status */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Clearing Status
              </div>
              {isEdit ? (
                <select
                  value={localClearingStatus}
                  onChange={(e) => setLocalClearingStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs sm:text-sm"
                  style={{ fontFamily: 'Lexend Deca', minWidth: '140px' }}
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="on_hold">On Hold</option>
                  <option value="cleared">Cleared</option>
                </select>
              ) : (
                <span 
                  className="inline-block text-xs font-medium"
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 500,
                    fontSize: '11px',
                    lineHeight: '20px',
                    color: statusColors.textColor,
                    backgroundColor: statusColors.bgColor,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    display: 'inline-block',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {formatClearingStatusLabel(shipmentData.status)}
                </span>
              )}
            </div>

            {/* Clearing Cost */}
            <div className="py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div 
                style={{
                  fontFamily: 'Lexend Deca',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '20px',
                  color: '#69697A'
                }}
              >
                Clearing Cost
              </div>
              {isEdit ? (
                <input
                  type="text"
                  value={localClearingCost}
                  onChange={(e) => setLocalClearingCost(e.target.value)}
                  placeholder="Enter amount"
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-xs sm:text-sm w-32 text-right"
                  style={{ fontFamily: 'Lexend Deca' }}
                />
              ) : (
                <div 
                  style={{
                    fontFamily: 'Lexend Deca',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '22px',
                    color: '#212126'
                  }}
                >
                  {shipmentData.clearingCost || '—'}
                </div>
              )}
            </div>

            {renderDocumentSection('Clearing Documents', 'clearing')}
            {renderDocumentSection('Bank Documents', 'bank')}
          </div>
        </div>

        {/* Action Bar */}
        <div 
          className="flex items-center justify-end gap-4 border-t border-gray-200 flex-shrink-0 px-4 sm:px-6"
          style={{
            width: '100%',
            height: '74px',
            backgroundColor: '#F2F4F2'
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
            style={{
              fontFamily: 'Lexend Deca',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '22px',
              color: '#212126',
              border: '1px solid #212126',
              backgroundColor: '#FFFFFF',
              height: '38px'
            }}
          >
            Cancel
          </button>
          {isEdit ? (
            <button
              type="button"
              onClick={handleSave}
              className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                color: '#FFFFFF',
                backgroundColor: '#212126',
                border: 'none',
                height: '38px'
              }}
            >
              Save Changes
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 rounded-lg hover:opacity-90 transition-opacity"
              style={{
                fontFamily: 'Lexend Deca',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                color: '#FFFFFF',
                backgroundColor: '#212126',
                border: 'none',
                height: '38px'
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(
    <>
      {modalContent}
      <AISummaryModal
        isOpen={aiSummaryOpen}
        onClose={() => setAiSummaryOpen(false)}
        entityType="clearing"
        entityId={shipmentData.id}
      />
    </>,
    document.body
  )
}

export default ClearingShipmentDetailsModal

